'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import { animated } from 'react-spring';
import {
  Info as InfoIcon,
  AlertTriangle as WarningIcon,
  CheckCircle as SuccessIcon,
  ChevronDown,
} from 'lucide-react';

import { BREAKPOINTS, HEADER_HEIGHT } from '@/constants';
import useBoop from '@/hooks/use-boop';

import {
  ICON_MAX_WIDTH_ARTICLE,
  ICON_MAX_WIDTH_TUTORIAL,
} from './Aside.constants';
import { TOC_THRESHOLD } from '@/components/ContentGrids/TutorialGrid';
import LeftEdgeCircular from './LeftEdgeCircular';
import LeftEdgeTriangular from './LeftEdgeTriangular';
import QuoteIcon from './QuoteIcon';

// Our Aside extends a standard <aside>. This HTML element already has a `title` attribute, which is expected to be a string, whereas our `Aside` component can accept a React element for the `title` prop. This little dance is required to make TypeScript happy:
type BaseAttributes = Omit<
  React.HTMLAttributes<HTMLBaseElement>,
  'title'
>;

interface Props extends BaseAttributes {
  type: 'info' | 'success' | 'warning' | 'quote';
  title?: React.ReactNode;
  children: React.ReactNode;
}

function Aside({
  type = 'info',
  title,
  children,
  ...delegated
}: Props) {
  let Component;
  let EdgeComponent = LeftEdgeCircular;
  let Icon;
  if (type === 'info') {
    Component = InfoAside;
    Icon = InfoIcon;
  } else if (type === 'success') {
    Component = SuccessAside;
    Icon = SuccessIcon;
  } else if (type === 'warning') {
    Component = WarningAside;
    EdgeComponent = LeftEdgeTriangular;
    Icon = WarningIcon;
  } else if (type === 'quote') {
    Component = QuoteAside;
    Icon = QuoteIcon;
  } else {
    console.error(`Unrecognized Aside type: ${type}`);
    Component = InfoAside;
    Icon = InfoIcon;
  }

  const [showMoreStyle, showMoreTrigger] = useBoop({ y: 4 });

  const titleId = useTitleId(title);

  const childArray = Array.isArray(children) ? children : [children];

  const expandedElem = childArray.find((child) => {
    return child?.type === 'expanded';
  });

  const [isExpanded, setIsExpanded] = React.useState(!expandedElem);

  const previewChildren = childArray.filter((child) => {
    // Sometimes, we want to *omit* a child in the preview area when it's been expanded. For example, instructions that say "Click to expand" are redundant when the content is already expanded.
    if (isExpanded && child?.props?.['data-hide-on-expand']) {
      return false;
    }

    return child?.type !== 'expanded';
  });

  const expandedChildren = expandedElem?.props?.children;

  return (
    <Component {...delegated}>
      <EdgeComponent />
      <IconWrapper>
        <Icon size={32} />
      </IconWrapper>
      {title && <Title id={titleId}>{title}</Title>}

      <Content>
        {previewChildren}
        {isExpanded ? (
          expandedChildren && <DropIn>{expandedChildren}</DropIn>
        ) : (
          <ShowMore
            onClick={() => setIsExpanded(true)}
            onMouseEnter={showMoreTrigger}
          >
            Show more{' '}
            <ShowMoreChevron style={showMoreStyle}>
              <ChevronDown size={20} />
            </ShowMoreChevron>
          </ShowMore>
        )}
      </Content>
    </Component>
  );
}

function useTitleId(title: React.ReactNode) {
  const uniqueId = React.useId();

  if (!title) {
    return undefined;
  }

  if (typeof title !== 'string') {
    return `aside-${uniqueId.replace(/:/, '')}`;
  }

  const sanitizedTitle = title
    .replace(/\s/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .toLowerCase();

  return `aside-${sanitizedTitle}`;
}

export const BaseWrapper = styled.aside`
  --aside-border-width: 3px;
  position: relative;
  margin-top: 32px;
  margin-inline: calc(var(--viewport-padding) * -1);
  margin-bottom: 64px;
  padding-block: 24px;
  padding-inline: var(--viewport-padding);
  padding-left: calc(
    var(--viewport-padding) - var(--aside-border-width)
  );
  border-left: var(--aside-border-width) solid transparent;
  border-radius: 0px 4px 4px 0px;
  font-size: 1rem;
  background-color: var(--color-background);
  transition: background-color var(--color-swap-duration);

  /* The "External" icon inside <Link> components feels a bit harsh inside asides. Soften it to a light gray: */
  --base-icon-color: var(--color-gray-800);

  /* If we have multiple asides in a row, stick them a bit closer together. */
  & + & {
    margin-top: -16px;
  }

  @media (max-width: 49rem) {
    /* Below this size, we're stuck to the edges of the viewport, so we don't need any rounding. */
    border-radius: 0px;
  }

  @media ${BREAKPOINTS.mdAndSmaller} {
    /*
      Optically, things look weird if there's an equal amount of space above and below. IDK why, but these numbers feel right:
    */
    padding-top: 16px;
    padding-bottom: 24px;
  }

  ::-webkit-scrollbar {
    width: 9px;
    height: 9px;
    background-color: var(--scrollbar-bg);
  }

  html::-webkit-scrollbar,
  body::-webkit-scrollbar {
    width: 12px;
    height: 12px;
    background-color: var(--scrollbar-bg);
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: var(--scrollbar-thumb);
    border: 2px solid var(--scrollbar-bg);
  }
  ::-webkit-scrollbar-thumb:hover {
    background-color: var(--scrollbar-thumb-hover);
  }
`;

export const InfoAside = styled(BaseWrapper)`
  --color-primary: var(--color-info-500);
  --color-background: var(--color-info-100);
`;

export const SuccessAside = styled(BaseWrapper)`
  --color-primary: var(--color-success-500);
  --color-background: var(--color-success-100);
`;

export const WarningAside = styled(BaseWrapper)`
  --color-primary: var(--color-warning-500);
  --color-background: var(--color-warning-100);
`;

export const QuoteAside = styled(BaseWrapper)`
  --color-primary: var(--color-info-400);
  --color-background: var(--color-info-100);
  font-style: italic;
  margin-top: 1rem;
`;

const Title = styled.strong`
  display: block;
  font-size: calc(18 / 16 * 1rem);
  margin-bottom: 0.5rem;
  font-weight: var(--font-weight-bold);
  scroll-margin-top: calc(${HEADER_HEIGHT}rem + 3rem);
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  /* Optical alignment */
  transform: translate(calc(-50% - 1.5px), -50%);
  padding: 8px;
  color: var(--color-primary);

  [data-layout='article'] & {
    @media (max-width: ${ICON_MAX_WIDTH_ARTICLE}rem) {
      display: none;
    }
  }

  /* So the funky thing about Tutorials is that when the TOC disappears, we suddenly have much more space, and can fit the icon! So we need to be very selective about our breakpoints */
  [data-layout='tutorial'] & {
    @media (max-width: ${ICON_MAX_WIDTH_TUTORIAL}rem) {
      display: none;
    }
    @media (max-width: ${TOC_THRESHOLD}rem) {
      display: block;
    }
    @media (max-width: ${ICON_MAX_WIDTH_ARTICLE}rem) {
      display: none;
    }
  }

  svg {
    display: block;
  }
`;

const Content = styled.div`
  /*
    HACK: So, the code snippets have a 'max-height' when we set 'clampMaxHeight' to true. This causes problems in Flow layout, the recursive height issue.

    Weirdly, this issue isn't present with CSS Grid, presumably because the height is calculated based on the grid row, rather than the parent element? Either way, works great!
  */
  display: grid;

  & > *:last-child {
    margin-bottom: 0;
  }
`;

const DropIn = styled.div`
  will-change: transform;
  animation: dropIn 350ms cubic-bezier(0, 0.66, 0.46, 0.98);

  & > *:last-child {
    margin-bottom: 0;
  }

  @keyframes dropIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0px);
    }
  }
`;

const ShowMore = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--color-text);
  font-weight: var(--font-weight-bold);
`;

const ShowMoreChevron = styled(animated.span)`
  display: block;
  height: 20px;
`;

export default Aside;
